import { useCallback, useEffect, useRef, useState } from "react";

/* ============================================================================
   SEGUROS-PLANES FASE 4 · borrador automático del constructor.

   POR QUÉ EN `localStorage` Y NO EN EL SERVIDOR: no hay dónde persistir un
   borrador sin inventar una tabla y unas reglas de negocio nuevas — `plan`
   solo conoce los estados "Activo" e "Inactivo", no existe "Borrador". El
   encargo prohíbe inventar reglas, así que el borrador vive en el navegador
   del usuario, que es exactamente el alcance del problema que resuelve: cerrar
   la pestaña por accidente y volver.

   La clave incluye el seguro Y el plan, así que dos planes a medias no se
   pisan, y el borrador de una aseguradora no aparece en otra.

   `localStorage` puede fallar (modo privado, cuota, políticas del navegador).
   Todo va envuelto: un borrador que no se puede guardar es una comodidad
   perdida, nunca un error que impida trabajar.
   ========================================================================== */

const PREFIJO = "plan-borrador";

export function claveBorrador(idseguro, idplan) {
    return `${PREFIJO}:${idseguro}:${idplan || "nuevo"}`;
}

export function leerBorrador(clave) {
    try {
        const crudo = window.localStorage.getItem(clave);
        if (!crudo) return null;
        const dato = JSON.parse(crudo);
        // Un borrador con forma inesperada (versión vieja, escritura a medias)
        // se ignora en silencio: recuperar basura es peor que no recuperar.
        if (!dato || typeof dato.nombre !== "string" || !Array.isArray(dato.puestos)) return null;
        return dato;
    } catch {
        return null;
    }
}

export function borrarBorrador(clave) {
    try {
        window.localStorage.removeItem(clave);
    } catch {
        /* sin borrador que borrar */
    }
}

/**
 * Guarda `datos` bajo `clave` con una pausa, para no escribir en cada tecla.
 * `activo` en false (mientras se carga, o tras guardar de verdad) no escribe
 * nada: si no, el propio vaciado del formulario al salir se guardaría como
 * borrador.
 */
export function useGuardadoBorrador(clave, datos, activo) {
    const [guardado, setGuardado] = useState(false);
    const primeraVez = useRef(true);

    useEffect(() => {
        if (!activo) return undefined;
        // El primer render solo refleja lo cargado; no es trabajo del usuario.
        if (primeraVez.current) {
            primeraVez.current = false;
            return undefined;
        }
        const t = setTimeout(() => {
            try {
                window.localStorage.setItem(clave, JSON.stringify(datos));
                setGuardado(true);
            } catch {
                setGuardado(false);
            }
        }, 600);
        return () => clearTimeout(t);
    }, [clave, datos, activo]);

    return guardado;
}

export function useBorrador(idseguro, idplan) {
    const clave = claveBorrador(idseguro, idplan);
    const olvidar = useCallback(() => borrarBorrador(clave), [clave]);
    return { clave, olvidar };
}
